'use client';

import { useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { SectionHeader, Grad } from '@/components/ui/Section';

/**
 * "Material Specifications" — dark technical-data interface.
 *
 * Left: vertical tab navigation (Physical / Chemical / Performance) with the
 * "Need Detailed Specs?" box beneath. Right: one table at a time with blue
 * monospace values, plus the Performance Comparison note.
 *
 * Grid placement is deliberate: on mobile the source order is header + tabs →
 * table → specs box, so the tabs sit directly above the data they control; on
 * desktop the box returns to its reference position under the tabs via
 * explicit row/column starts.
 *
 * All specification values are the section's existing data — nothing added,
 * nothing hidden. Tab switches remount the data region with the site's `.m-fade`
 * (disabled under prefers-reduced-motion).
 */

interface Specification {
  id: string;
  property: string;
  value: string;
  unit: string;
  description: string;
}

type TabKey = 'physical' | 'chemical' | 'performance';

const specifications: Record<TabKey, Specification[]> = {
  physical: [
    {
      id: 'spec_purity',
      property: 'Purity',
      value: '99.5',
      unit: '%',
      description: 'High-grade graphene with minimal impurities',
    },
    {
      id: 'spec_bulk_density',
      property: 'Bulk Density',
      value: '0.006',
      unit: 'gram/cc',
      description: 'Ultra-low density for efficient dispersion and maximum volume coverage',
    },
    {
      id: 'spec_surface_area',
      property: 'Surface Area',
      value: '170 m²/g [BET] & 750 m²/g [SSA]',
      unit: '',
      description: 'Maximum active surface for bonding',
    },
    {
      id: 'spec_thickness',
      property: 'Thickness',
      value: '0.8-2',
      unit: 'nm',
      description: 'Single, bilayer, tri-layers and few-layer graphene',
    },
  ],
  chemical: [
    {
      id: 'spec_carbon_content',
      property: 'Carbon Content',
      value: '>99',
      unit: '%',
      description: 'Pure carbon structure',
    },
    {
      id: 'spec_oxygen_content',
      property: 'Oxygen Content',
      value: '<0.5',
      unit: '%',
      description: 'Minimal oxidation',
    },
    {
      id: 'spec_ph_value',
      property: 'pH Value',
      value: '6.5-7.5',
      unit: '',
      description: 'Neutral pH for compatibility (Customizable)',
    },
    {
      id: 'spec_moisture',
      property: 'Moisture Content',
      value: '<1',
      unit: '%',
      description: 'Dry powder form',
    },
  ],
  performance: [
    {
      id: 'spec_tensile_strength',
      property: 'Tensile Strength',
      value: '130',
      unit: 'GPa',
      description: 'Exceptional mechanical strength',
    },
    {
      id: 'spec_thermal_conductivity',
      property: 'Thermal Conductivity',
      value: '5000',
      unit: 'W/mK',
      description: 'Superior heat dissipation',
    },
    {
      id: 'spec_electrical_conductivity',
      property: 'Electrical Conductivity',
      value: '10⁶',
      unit: 'S/m',
      description: 'Excellent electron mobility',
    },
    {
      id: 'spec_elastic_modulus',
      property: 'Elastic Modulus',
      value: '1',
      unit: 'TPa',
      description: 'Maintains flexibility under stress',
    },
  ],
};

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'physical', label: 'Physical Properties', icon: 'CubeIcon' },
  { key: 'chemical', label: 'Chemical Properties', icon: 'BeakerIcon' },
  { key: 'performance', label: 'Performance Metrics', icon: 'ChartBarIcon' },
];

const PANEL = '#0a1322';

const TechnicalSpecs = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('physical');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const rows = specifications[activeTab];

  /** Vertical tablist: Up/Down (and Left/Right) cycle selection + focus. */
  const onTablistKeyDown = (e: React.KeyboardEvent) => {
    const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const backward = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
    if (!forward && !backward) return;
    e.preventDefault();
    const i = tabs.findIndex((t) => t.key === activeTab);
    const next = forward ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
    setActiveTab(tabs[next].key);
    tabRefs.current[next]?.focus();
  };

  const valueHeading = `Value${activeTab === 'performance' ? ' (Approx.)' : ''}`;

  return (
    <section className="section relative overflow-hidden bg-ink text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(65rem_30rem_at_18%_-12%,rgba(14,165,233,0.1),transparent_60%)]"
      />

      <div className="container-page relative">
        {/* Centred header — standardised presentation. */}
        <SectionHeader
          title={
            <>
              Material <Grad>Specifications</Grad>
            </>
          }
          eyebrow="Technical Data"
          lead="Comprehensive technical specifications for our various graphene, reduced graphene oxide and graphene oxide products"
        />

        {/* Tabs and table now start from the same grid top. */}
        <div className="mt-11 grid gap-8 lg:grid-cols-12 lg:gap-10">
          {/* ── Vertical tabs ──────────────────────────────────────────── */}
          <div className="min-w-0 lg:col-span-4">
            <div
              role="tablist"
              aria-label="Specification categories"
              aria-orientation="vertical"
              onKeyDown={onTablistKeyDown}
              className="flex flex-col gap-3"
            >
              {tabs.map((tab, i) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    ref={(el) => {
                      tabRefs.current[i] = el;
                    }}
                    role="tab"
                    id={`spec-tab-${tab.key}`}
                    aria-selected={isActive}
                    aria-controls="spec-panel"
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-[border-color,background-color,box-shadow] duration-base ease-out active:translate-y-px motion-reduce:active:transform-none sm:p-5 ${
                      isActive
                        ? 'bg-accent-rich border-sky-400/60 text-white shadow-[0_10px_30px_rgba(14,165,233,0.3)]'
                        : 'border-white/10 bg-white/[0.04] text-slate-300 hover:border-accent/45 hover:bg-white/[0.07] hover:text-white'
                    }`}
                  >
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
                        isActive ? 'bg-white/15 text-white' : 'bg-accent/10 text-accent'
                      }`}
                    >
                      <Icon name={tab.icon} size={22} />
                    </span>
                    <span className="text-[0.9375rem] font-semibold">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Data panel ─────────────────────────────────────────────── */}
          <div
            id="spec-panel"
            role="tabpanel"
            aria-labelledby={`spec-tab-${activeTab}`}
            className="min-w-0 lg:col-span-8 lg:row-span-2"
          >
            {/* Desktop table */}
            <div
              className="hidden overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(2,8,23,0.8),0_0_50px_-12px_rgba(14,165,233,0.15)] md:block"
              style={{ backgroundColor: PANEL }}
            >
              <div className="overflow-x-auto">
                <table key={activeTab} className="m-fade w-full min-w-[640px] table-fixed">
                  <caption className="sr-only">
                    {tabs.find((t) => t.key === activeTab)?.label} for Micratto graphene products
                  </caption>
                  <colgroup>
                    <col style={{ width: '26%' }} />
                    <col style={{ width: '34%' }} />
                    <col style={{ width: '40%' }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-6 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                        Property
                      </th>
                      <th className="px-6 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                        {valueHeading}
                      </th>
                      <th className="px-6 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((spec) => (
                      <tr
                        key={spec.id}
                        className="border-b border-white/[0.06] transition-colors duration-fast last:border-0 hover:bg-white/[0.03]"
                      >
                        <th
                          scope="row"
                          className="px-6 py-5 text-left align-middle text-[0.9375rem] font-semibold text-white"
                        >
                          {spec.property}
                        </th>
                        <td className="px-6 py-5 align-middle">
                          <span className="break-words font-mono text-[0.9375rem] font-bold text-sky-400">
                            {spec.value}
                            {spec.unit && (
                              <span className="ml-1.5 font-medium text-slate-400">{spec.unit}</span>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-5 align-middle text-sm leading-relaxed text-slate-400">
                          {spec.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile stacked cards — same data, nothing hidden */}
            <div key={`m-${activeTab}`} className="m-fade flex flex-col gap-4 md:hidden">
              {rows.map((spec) => (
                <div
                  key={spec.id}
                  className="rounded-xl border border-white/10 p-5"
                  style={{ backgroundColor: PANEL }}
                >
                  <p className="text-base font-semibold text-white">{spec.property}</p>
                  <p className="mt-2 break-words font-mono text-lg font-bold text-sky-400">
                    {spec.value}
                    {spec.unit && (
                      <span className="ml-1.5 text-sm font-medium text-slate-400">{spec.unit}</span>
                    )}
                  </p>
                  {activeTab === 'performance' && (
                    <p className="mt-0.5 text-[0.6875rem] uppercase tracking-wider text-slate-500">
                      Approx.
                    </p>
                  )}
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">
                    {spec.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Performance comparison note */}
            <div className="mt-6 rounded-xl border border-accent/25 bg-accent/[0.06] p-6">
              <div className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent/50 text-accent">
                  <Icon name="InformationCircleIcon" size={22} />
                </span>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-white">Performance Comparison</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-300">
                    These specifications represent typical values. Graphene additives can achieve
                    40-60% performance improvements compared to traditional materials in most
                    applications.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── "Need Detailed Specs?" — under the tabs on desktop, after the
                 data on mobile so tabs stay adjacent to their table. ──────── */}
          <div className="min-w-0 lg:col-span-4 lg:col-start-1 lg:row-start-2 lg:self-start">
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
              <h3 className="text-base font-semibold text-white">Need Detailed Specs?</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                Reach out to get our complete technical datasheet
              </p>
              <Link
                href="/contact"
                className="mt-5 inline-flex min-h-[46px] w-full items-center justify-center gap-2.5 rounded-lg border border-accent/50 bg-accent/[0.08] px-5 text-sm font-semibold text-white transition-[border-color,background-color] duration-fast ease-out hover:border-accent hover:bg-accent/20 active:translate-y-px motion-reduce:active:transform-none"
              >
                <Icon name="EnvelopeIcon" size={18} className="text-accent" />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnicalSpecs;
